import React, { useState, useEffect, useRef, useId } from 'react';

const Tooltip = ({ text, children, position = 'right', className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);
  const tooltipId = useId();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const showTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, 200); // Small delay
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  // Close tooltip on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isVisible && e.key === 'Escape') {
        hideTooltip();
      }
    };

    if (isVisible) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible]);

  const getTooltipClasses = () => {
    const base = "absolute z-[9999] px-2 py-1 text-xs font-medium text-white bg-charcoal-900 rounded shadow-sm pointer-events-none animate-fade-in whitespace-nowrap";
    switch (position) {
      case 'top':
        return `${base} bottom-full left-1/2 -translate-x-1/2 mb-2`;
      case 'bottom':
        return `${base} top-full left-1/2 -translate-x-1/2 mt-2`;
      case 'left':
        return `${base} right-full top-1/2 -translate-y-1/2 mr-2`;
      case 'right':
      default:
        return `${base} left-full top-1/2 -translate-y-1/2 ml-2`;
    }
  };

  const getArrowClasses = () => {
    const base = "absolute w-0 h-0 border-4 border-transparent";
    switch (position) {
      case 'top':
        return `${base} -bottom-[8px] left-1/2 -translate-x-1/2 border-t-charcoal-900`;
      case 'bottom':
        return `${base} -top-[8px] left-1/2 -translate-x-1/2 border-b-charcoal-900`;
      case 'left':
        return `${base} -right-[8px] top-1/2 -translate-y-1/2 border-l-charcoal-900`;
      case 'right':
      default:
        return `${base} -left-[8px] top-1/2 -translate-y-1/2 border-r-charcoal-900`;
    }
  };

  return (
    <div
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      className={`relative inline-flex items-center justify-center ${className}`}
      aria-describedby={isVisible ? tooltipId : undefined}
    >
      {children}
      {isVisible && (
        <div
          id={tooltipId}
          role="tooltip"
          className={getTooltipClasses()}
        >
          {text}
          {/* Arrow */}
          <div className={getArrowClasses()} />
        </div>
      )}
    </div>
  );
};

export default Tooltip;
